import { CheckCircle, AlertCircle, UserCheck } from 'lucide-react';
import type { AttendanceRecord } from '../../types/attendance';
import type { AttendanceMode } from '../../utils/attendanceMode';
import { formatTimeInBusinessTZ } from '../../utils/date';
import { useFlag } from '../../contexts/FeatureFlagOverrideContext';
import settings from '../../config/settings.json';

const BASE = 'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium';

/** The flag key both components in this file read. */
const PICKUP_DETAILS_FLAG = 'parentPickupDetails';

/**
 * Whether the parent surfaces may report the end of a session as well as its start.
 *
 * The canonical value is read at render rather than at import: several page tests mock
 * `settings.json` with a getter over a `const` declared further down the file, and touching
 * it while this module initialises hits that binding's temporal dead zone.
 *
 * `=== true` rather than a bare read, for those same partial mocks — a missing key must
 * resolve to off rather than to an `undefined` that `useFlag` would hand straight back.
 */
function usePickupDetails(): boolean {
  return useFlag(PICKUP_DETAILS_FLAG, (settings as Record<string, unknown>)[PICKUP_DETAILS_FLAG] === true);
}

/**
 * One recorded state, phrased for the parent rather than the teacher.
 *
 * `AttendanceStatusBadge` says "Checked in" because a teacher working a roster already knows
 * the time — they are standing there. A parent is reading this from an office and the time is
 * most of the answer, so it goes inside the pill: "Checked in 12:04 PM".
 *
 * **By default the parent surfaces report the arrival and nothing else.** A check-out is
 * recorded on the same row and is deliberately not rendered: a parent's question is whether
 * their child got there, and the check-out half of the row is not dependable enough to answer
 * anything with. A teacher in the middle of a pickup line marks children out late, in batches,
 * or not at all, so "no check-out yet" routinely describes a child who went home an hour ago.
 * A green arrival pill that never changes is the honest reading of a row whose second half we
 * cannot stand behind — and it is stable, which matters on a card a parent glances at: the
 * pill they saw at 3pm still says the same thing at 6pm.
 *
 * `parentPickupDetails` (default off) restores the fuller reading for a session where the
 * check-out *is* trusted — an admin can switch it on to see what parents would get, and it is
 * the switch to flip if check-out discipline improves enough to be worth showing. With it on,
 * a collected child reads "Picked up by 外婆" and `ParentAttendanceTimes` prints both ends of
 * the day beneath the class.
 *
 * `mode` only matters while that flag is on, and it is what keeps an enrichment class honest
 * there. Chess and piano run inside the building the children are already in; at the end the
 * teacher marks the session done and they go back to after-school. There is no adult to name,
 * so a `picked_up` row on one of those reads as "Attended" — the session finished — and never
 * as a supervised handover. Only a pickup-mode program is allowed the sentence with a person
 * in it.
 *
 * `not_arrived` returns null rather than a further grey pill: it is the state a roster row is
 * born in, indistinguishable from "the teacher has not tapped yet", and a parent reading "not
 * arrived" about a child who has been in the room for an hour is worse than reading nothing.
 * Callers filter it out before they get here; this is the second line of defence.
 */
export function ParentAttendancePill({
  record,
  mode = 'pickup',
}: {
  record: AttendanceRecord;
  mode?: AttendanceMode;
}) {
  const showPickup = usePickupDetails();

  // The exception to reporting the arrival alone: a check-out the parent made themselves from
  // the pickup screen. That half of the row is not staff discipline — the person it is about
  // wrote it — so it is shown with the flag off too. With the flag on, the fuller branch below
  // names the collector as it does for any other handover.
  if (!showPickup && mode === 'pickup' && record.status === 'picked_up' && record.checkedOutBy === 'parent') {
    return (
      <span className={`${BASE} bg-blue-100 text-blue-800`}>
        <UserCheck className="h-3 w-3" />
        {record.pickedUpAt
          ? `Checked out ${formatTimeInBusinessTZ(record.pickedUpAt, { hour12: true })}`
          : 'Checked out'}
      </span>
    );
  }

  // With the flag off `picked_up` shares the arrival branch: it is a checked-in child whose
  // session has since been closed, and closing it does not unmake the arrival being asked about.
  if (record.status === 'checked_in' || (record.status === 'picked_up' && !showPickup)) {
    return (
      <span className={`${BASE} bg-green-100 text-green-800`}>
        <CheckCircle className="h-3 w-3" />
        {record.checkedInAt
          ? `Checked in ${formatTimeInBusinessTZ(record.checkedInAt, { hour12: true })}`
          : 'Checked in'}
      </span>
    );
  }

  if (record.status === 'absent') {
    return (
      <span className={`${BASE} bg-amber-100 text-amber-800`}>
        <AlertCircle className="h-3 w-3" />
        {record.absenceReason ? `Absent · ${record.absenceReason}` : 'Marked absent'}
      </span>
    );
  }

  if (record.status === 'picked_up') {
    if (mode === 'class') {
      return (
        <span className={`${BASE} bg-blue-50 text-blue-700`}>
          <CheckCircle className="h-3 w-3" />
          Attended
        </span>
      );
    }
    return (
      <span className={`${BASE} bg-blue-100 text-blue-800`}>
        <UserCheck className="h-3 w-3" />
        {record.pickedUpByName ? `Picked up by ${record.pickedUpByName}` : 'Picked up'}
      </span>
    );
  }

  return null;
}

/**
 * The times either side of a finished session — "In 12:04 PM · Out 5:41 PM".
 *
 * Renders only while `parentPickupDetails` is on; see `ParentAttendancePill` for why the
 * check-out is off by default. With the flag off this is nothing at all rather than the
 * arrival alone: the pill beside it is already carrying the arrival time, and printing it
 * twice on one row is noise.
 *
 * Grey and unpilled, deliberately. A pill is a state affordance and there is only one state
 * here — putting the arrival in a second green pill beside a blue one would read as a child
 * who is somehow both checked in and gone. These are the clock readings behind the state the
 * pill already named.
 *
 * A class session gets its arrival and not its ending. "Out" on a chess class would say the
 * child left, when what happened is that the class finished and they went back to
 * after-school — the same distinction that turns its pill into "Attended".
 *
 * Nothing is printed for a child who is still checked in: their pill already carries the
 * arrival time, and repeating it beside itself is noise.
 */
export function ParentAttendanceTimes({
  record,
  mode = 'pickup',
  className = '',
}: {
  record: AttendanceRecord;
  mode?: AttendanceMode;
  className?: string;
}) {
  const showPickup = usePickupDetails();
  if (!showPickup) return null;
  if (record.status !== 'picked_up') return null;

  const parts: string[] = [];
  if (record.checkedInAt) parts.push(`In ${formatTimeInBusinessTZ(record.checkedInAt, { hour12: true })}`);
  if (mode === 'pickup' && record.pickedUpAt) {
    parts.push(`Out ${formatTimeInBusinessTZ(record.pickedUpAt, { hour12: true })}`);
  }
  if (parts.length === 0) return null;

  return (
    <span className={`whitespace-nowrap text-xs tabular-nums text-gray-500 ${className}`}>
      {parts.join(' · ')}
    </span>
  );
}
